/* =====================================================================
   PG Motion Upgrade (30 Oct 2026, preview theme only). See pg-motion.css.
   Plain script, no libraries, loaded with defer so it never holds up the
   page. Every effect checks for reduced motion first.
   ===================================================================== */
(function () {
  'use strict';
  var d = document;
  var html = d.documentElement;
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || d).querySelectorAll(sel)); };

  if (hasIO) html.classList.add('pgm');

  /* One observer for everything that animates on arrival.
     Plus a safety net: anything still waiting that is already above the bottom of the screen
     (a fast fling, a jump link, or the browser restoring the scroll position) is shown at once,
     so nothing can ever be left invisible. */
  var pending = [];
  function reveal(el) {
    if (el.classList.contains('pgm-in')) return;
    el.classList.add('pgm-in');
    if (io) io.unobserve(el);
    var i = pending.indexOf(el);
    if (i > -1) pending.splice(i, 1);
    if (typeof el._pgmIn === 'function') el._pgmIn();
  }
  var io = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) reveal(e.target); });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0 }) : null;

  function watch(el, fn) {
    if (fn) el._pgmIn = fn;
    if (io) { io.observe(el); pending.push(el); } else { el.classList.add('pgm-in'); if (fn) fn(); }
  }
  var sweepQueued = false;
  function sweep() {
    sweepQueued = false;
    var limit = window.innerHeight * 0.95;
    pending.slice().forEach(function (el) {
      if (el.getBoundingClientRect().top < limit) reveal(el);
    });
  }
  window.addEventListener('scroll', function () {
    if (!sweepQueued && pending.length) { sweepQueued = true; setTimeout(function () { requestAnimationFrame(sweep); }, 90); }
  }, { passive: true });
  window.addEventListener('load', function () { setTimeout(sweep, 300); });

  /* ---------- 1 and 2. Section reveals and banner settle-in ---------- */
  function setupSections() {
    if (!io || reduce) return;
    var vh = window.innerHeight;
    $$('#MainContent > .shopify-section, .shopify-section-group-footer-group').forEach(function (sec) {
      var top = sec.getBoundingClientRect().top;
      var hasBanner = !!sec.querySelector('.banner');
      if (top < vh * 0.92) {
        if (hasBanner) sec.classList.add('pgm-hero');
        return; /* already on screen: leave it exactly as it is */
      }
      if (hasBanner) { sec.classList.add('pgm-banner'); watch(sec); return; }
      if (sec.querySelector('.scroll-trigger')) return; /* Dawn already animates it */
      if (sec.offsetHeight < 8) return;
      sec.classList.add('pgm-rise');
      watch(sec);
    });
  }

  /* ---------- 3. Line icons draw in ---------- */
  function setupIcons() {
    if (!io || reduce) return;
    $$('.pg-trust__icon, [data-pgm-draw]').forEach(function (svg) {
      if (svg.classList.contains('pgm-draw')) return;
      $$('path, circle, line, polyline', svg).forEach(function (p) { p.setAttribute('pathLength', '1'); });
      svg.classList.add('pgm-draw');
      watch(svg);
    });
  }

  /* ---------- 4. Count-up numbers ---------- */
  function countUp(el) {
    if (el.dataset.pgmDone) return;
    el.dataset.pgmDone = '1';
    var raw = (el.dataset.pgmCount || el.textContent).trim();
    var m = raw.match(/^(\d{1,3}(?:,\d{3})*|\d+)(\.\d+)?$/);
    if (!m) return;
    var target = parseFloat(raw.replace(/,/g, ''));
    var dec = m[2] ? m[2].length - 1 : 0;
    var comma = raw.indexOf(',') > -1;
    /* 4 Nov 2026 (Daniel): a rating (a number with decimals, like 4.98) is shown as it is, never counted up from 0, so it never reads as a low score mid-animation */
    if (reduce || target === 0 || dec > 0) { el.textContent = raw; return; }
    el.classList.add('pgm-counting');
    var start = null, dur = target < 20 ? 1100 : 1500;
    function fmt(v) {
      var s = v.toFixed(dec);
      if (comma) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return s;
    }
    el.textContent = fmt(0);
    function step(t) {
      if (start === null) start = t;
      var k = Math.min(1, (t - start) / dur);
      var eased = 1 - Math.pow(1 - k, 3);
      el.textContent = k < 1 ? fmt(target * eased) : raw;
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function setupCounts() {
    var list = $$('[data-pgm-count], .pg-rating b');
    /* Judge.me summaries load late; pick out any element that is just a number */
    $$('.jdgm-rev-widg__summary *, .jdgm-all-reviews-widget .jdgm-all-reviews__header *, .jdgm-revs-tab__header *, .jdgm-carousel-title-and-link *').forEach(function (el) {
      if (el.children.length) return;
      if (/^\d+(\.\d+)?$/.test(el.textContent.trim())) list.push(el);
    });
    list.forEach(function (el) {
      if (el.dataset.pgmSeen) return;
      el.dataset.pgmSeen = '1';
      watch(el, function () { countUp(el); });
    });
  }

  /* ---------- 5. Header state and progress line ---------- */
  function setupHeader() {
    var header = d.querySelector('.section-header');
    var bar = null;
    if (!reduce) {
      bar = d.createElement('div');
      bar.className = 'pgm-progress' + (d.querySelector('.article-template__content') ? ' pgm-progress--read' : '');
      bar.setAttribute('aria-hidden', 'true');
      d.body.appendChild(bar);
    }
    var ticking = false;
    function update() {
      ticking = false;
      var y = window.scrollY || window.pageYOffset;
      if (header) header.classList.toggle('pgm-scrolled', y > 12);
      if (bar) {
        /* 1 Nov: on blog articles the line follows the article itself, so it reads as a reading progress line */
        var art = d.querySelector('.article-template__content');
        if (art) {
          var r = art.getBoundingClientRect(), top = r.top + y, len = r.height - window.innerHeight * 0.6;
          bar.style.setProperty('--p', len > 0 ? Math.max(0, Math.min(1, (y - top + window.innerHeight * 0.3) / len)).toFixed(4) : 0);
        } else {
          var max = d.documentElement.scrollHeight - window.innerHeight;
          bar.style.setProperty('--p', max > 0 ? Math.min(1, y / max).toFixed(4) : 0);
        }
      }
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- 6. Light sweep on the solid buy buttons ---------- */
  var SHEEN = '.product-form__submit, .pg-sticky__button, #CartDrawer-Checkout, .cart__checkout-button';
  function addSheen(btn) {
    if (!btn || btn.querySelector(':scope > .pgm-sheen')) return;
    var s = d.createElement('span');
    s.className = 'pgm-sheen';
    s.setAttribute('aria-hidden', 'true');
    btn.appendChild(s);
  }
  function setupSheen() {
    if (reduce) return;
    $$(SHEEN).forEach(addSheen);
    d.addEventListener('pointerover', function (e) {
      var b = e.target.closest && e.target.closest(SHEEN);
      if (b) addSheen(b);
    }, { passive: true });
  }

  /* ---------- 7. Smooth dropdowns ---------- */
  function setupAccordions() {
    if (reduce || !Element.prototype.animate) return;
    /* 31 Oct (Daniel): smoother drop-downs. The pill grows to its new height while the answer fades
       and settles into place; closing reverses it, with the answer fading out first. */
    var EASE = 'cubic-bezier(.22,.61,.36,1)';
    $$('.accordion details, .product__accordion details').forEach(function (det) {
      if (det._pgmAcc) return;
      det._pgmAcc = true;
      var sum = det.querySelector(':scope > summary');
      if (!sum) return;
      var body = function () {
        var kids = Array.prototype.filter.call(det.children, function (c) { return c !== sum; });
        return kids;
      };
      sum.addEventListener('click', function (e) {
        e.preventDefault();
        var startH = det.offsetHeight;
        var closing = det._anim ? det._dir === 'open' : det.open;
        if (det._anim) det._anim.cancel();
        det._dir = closing ? 'close' : 'open';
        det.style.overflow = 'hidden';
        var endH;
        if (closing) {
          endH = sum.offsetHeight;
          body().forEach(function (k) { k.animate({ opacity: [1, 0], transform: ['none', 'translateY(-4px)'] }, { duration: 200, easing: 'ease-out', fill: 'forwards' }); });
        } else {
          det.open = true;
          endH = det.offsetHeight;
          body().forEach(function (k) { k.animate({ opacity: [0, 1], transform: ['translateY(-6px)', 'none'] }, { duration: 420, delay: 90, easing: EASE, fill: 'backwards' }); });
        }
        var dur = closing ? 340 : 460;
        var a = det.animate({ height: [startH + 'px', endH + 'px'] }, { duration: dur, easing: EASE });
        det._anim = a;
        var done = false;
        function finish() {
          if (done || det._anim !== a) return;
          done = true;
          if (closing) det.open = false;
          det._anim = null;
          det.style.overflow = '';
          body().forEach(function (k) { k.getAnimations && k.getAnimations().forEach(function (x) { x.cancel(); }); });
        }
        a.onfinish = finish;
        a.oncancel = function () { if (det._anim === a) { det._anim = null; det.style.overflow = ''; } };
        /* Belt and braces: if the browser skips animation events, settle anyway */
        setTimeout(function () { if (det._anim === a) { a.cancel(); det._anim = a; finish(); } }, dur + 140);
      });
    });
  }

  /* ---------- 8. Ingredient hover cards ---------- */
  var TERMS = {
    pap: { name: 'PAP+', text: 'Our peroxide-free whitening agent (phthalimidoperoxycaproic acid). It lifts surface stains without the sensitivity peroxide can cause.' },
    hydroxyapatite: { name: 'Hydroxyapatite', text: 'The mineral your enamel is made from. It helps support and replenish the enamel surface while you whiten.' },
    xylitol: { name: 'Xylitol', text: 'A sugar-free sweetener in the formula, part of its fresh mint taste.' }
  };
  var TERM_RE = /(^|[^\w])(PAP\+?|[Hh]ydroxyapatite|[Xx]ylitol)(?![\w])/;
  var SKIP = 'a, h1, h2, h3, h4, h5, h6, button, summary, label, select, option, script, style, .price, .card__heading, .pgm-term, [contenteditable], .jdgm-widget';
  var tip = null, tipFor = null;

  function keyOf(word) {
    var w = word.toLowerCase();
    return w.indexOf('pap') === 0 ? 'pap' : w;
  }
  function wrapTerms(root, done) {
    var walker = d.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (node) {
      var parent = node.parentElement;
      if (!parent || parent.closest(SKIP)) return;
      var m = node.nodeValue.match(TERM_RE);
      if (!m) return;
      var key = keyOf(m[2]);
      if (done[key]) return;
      done[key] = true;
      var startIdx = m.index + m[1].length;
      var after = node.splitText(startIdx);
      after.splitText(m[2].length);
      var span = d.createElement('span');
      span.className = 'pgm-term';
      span.tabIndex = 0;
      span.setAttribute('role', 'button');
      span.setAttribute('aria-label', m[2] + ': what is it?');
      span.dataset.pgmTerm = key;
      span.textContent = m[2];
      after.parentNode.replaceChild(span, after);
    });
  }
  function showTip(term) {
    var t = TERMS[term.dataset.pgmTerm];
    if (!t) return;
    if (!tip) {
      tip = d.createElement('div');
      tip.className = 'pgm-tip';
      tip.id = 'pgm-tip';
      tip.setAttribute('role', 'tooltip');
      d.body.appendChild(tip);
    }
    if (tipFor && tipFor !== term) tipFor.classList.remove('is-open');
    tipFor = term;
    term.classList.add('is-open');
    term.setAttribute('aria-describedby', 'pgm-tip');
    tip.innerHTML = '<span class="pgm-tip__name"></span><span class="pgm-tip__text"></span>';
    tip.firstChild.textContent = t.name;
    tip.lastChild.textContent = t.text;
    var r = term.getBoundingClientRect();
    var w = tip.offsetWidth, h = tip.offsetHeight, vw = window.innerWidth;
    var left = Math.max(12, Math.min(vw - w - 12, r.left + r.width / 2 - w / 2));
    var top = r.top - h - 10;
    if (top < 70) top = r.bottom + 10;
    tip.style.left = left + 'px';
    tip.style.top = top + 'px';
    tip.classList.add('is-on');
  }
  function hideTip() {
    if (tip) tip.classList.remove('is-on');
    if (tipFor) { tipFor.classList.remove('is-open'); tipFor.removeAttribute('aria-describedby'); }
    tipFor = null;
  }
  function setupTerms() {
    $$('.rte, .multicolumn-card__info, .pg-home-intro, .accordion__content').forEach(function (el) {
      if (el._pgmTerms) return;
      el._pgmTerms = true;
      /* One hover card per ingredient per section, so it never feels busy */
      var sec = el.closest('.shopify-section') || d.body;
      sec._pgmDone = sec._pgmDone || {};
      wrapTerms(el, sec._pgmDone);
    });
    if (setupTerms.bound) return;
    setupTerms.bound = true;
    var finePointer = window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (finePointer) {
      d.addEventListener('mouseover', function (e) {
        var t = e.target.closest && e.target.closest('.pgm-term');
        if (t) showTip(t);
      });
      d.addEventListener('mouseout', function (e) {
        var t = e.target.closest && e.target.closest('.pgm-term');
        if (t && !t.contains(e.relatedTarget)) hideTip();
      });
    }
    d.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('.pgm-term');
      if (t) {
        e.preventDefault();
        e.stopPropagation();
        if (tipFor === t && !finePointer) hideTip(); else showTip(t);
      } else if (tipFor) {
        hideTip();
      }
    }, true);
    d.addEventListener('focusin', function (e) {
      if (e.target.classList && e.target.classList.contains('pgm-term')) showTip(e.target);
    });
    d.addEventListener('focusout', function (e) {
      if (e.target.classList && e.target.classList.contains('pgm-term')) hideTip();
    });
    d.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hideTip();
      if ((e.key === 'Enter' || e.key === ' ') && e.target.classList && e.target.classList.contains('pgm-term')) {
        e.preventDefault();
        showTip(e.target);
      }
    });
    window.addEventListener('scroll', hideTip, { passive: true });
    window.addEventListener('resize', hideTip);
  }

  /* ---------- 9. Before / after slider ---------- */
  function setupSlider(root) {
    if (root._pgm) return;
    root._pgm = true;
    var frame = root.querySelector('.pgm-ba__frame');
    if (!frame) { var st0 = root.querySelector('.pgm-stats'); if (st0) watch(st0); return; } /* 2 Nov: photos removed, stats only */
    var range = root.querySelector('.pgm-ba__range');
    var tag = root.querySelector('[data-pgm-tag]');
    var touched = false;
    function setPos(v) {
      v = Math.max(0, Math.min(100, v));
      frame.style.setProperty('--pos', v + '%');
      range.value = Math.round(v);
    }
    function fromEvent(e) {
      var r = frame.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    }
    var dragging = false, startX = 0, startY = 0, decided = false;
    frame.addEventListener('pointerdown', function (e) {
      touched = true;
      dragging = true;
      decided = e.pointerType === 'mouse';
      startX = e.clientX; startY = e.clientY;
      if (decided) {
        frame.classList.add('is-drag');
        setPos(fromEvent(e));
        try { frame.setPointerCapture(e.pointerId); } catch (err) {}
      }
    });
    frame.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      if (!decided) {
        var dx = Math.abs(e.clientX - startX), dy = Math.abs(e.clientY - startY);
        if (dx < 4 && dy < 4) return;
        if (dy > dx) { dragging = false; return; } /* a scroll, let the page move */
        decided = true;
        frame.classList.add('is-drag');
        try { frame.setPointerCapture(e.pointerId); } catch (err) {}
      }
      setPos(fromEvent(e));
    });
    function end(e) {
      if (dragging && !decided && e && e.type === 'pointerup') setPos(fromEvent(e)); /* a tap jumps the handle */
      dragging = false;
      frame.classList.remove('is-drag');
    }
    frame.addEventListener('pointerup', end);
    frame.addEventListener('pointercancel', end);
    range.addEventListener('input', function () { touched = true; setPos(+range.value); });

    /* Timeline: Day 1 shows the before photo in full, Day 3 to 5 and Day 14 sweep to that after photo */
    var anim = null;
    function animateTo(to, ms) {
      if (anim) cancelAnimationFrame(anim);
      var from = +range.value, t0 = null;
      if (reduce) { setPos(to); return; }
      function step(t) {
        if (t0 === null) t0 = t;
        var k = Math.min(1, (t - t0) / ms);
        var e = 1 - Math.pow(1 - k, 3);
        setPos(from + (to - from) * e);
        if (k < 1) anim = requestAnimationFrame(step); else anim = null;
      }
      anim = requestAnimationFrame(step);
    }
    var tl = root.querySelector('.pgm-tl');
    var stops = $$('[data-pgm-day]', root);
    var NAMES = ['Day 1', 'Day 3 to 5', 'Day 14'];
    stops.forEach(function (stop) {
      stop.addEventListener('click', function () {
        touched = true;
        var day = +stop.dataset.pgmDay;
        stops.forEach(function (s2) {
          var n = +s2.dataset.pgmDay;
          s2.classList.toggle('is-on', n === day);
          s2.classList.toggle('is-done', n <= day);
          s2.setAttribute('aria-pressed', n === day ? 'true' : 'false');
        });
        if (tl) tl.style.setProperty('--tl', (day * 50) + '%');
        if (day > 0) {
          $$('[data-pgm-after]', root).forEach(function (img) {
            img.classList.toggle('is-on', +img.dataset.pgmAfter === day);
          });
          if (tag) tag.textContent = NAMES[day];
          animateTo(4, 900);
        } else {
          animateTo(100, 900);
        }
      });
    });
    frame.addEventListener('pointerdown', function () { if (anim) { cancelAnimationFrame(anim); anim = null; } });

    /* A short hint sweep the first time it comes into view, so people know it moves */
    if (!reduce) {
      watch(frame, function () {
        setTimeout(function () {
          if (touched) return;
          var keys = [50, 74, 28, 50], i = 0, from = 50, t0 = null, seg = 650;
          function step(t) {
            if (touched) return;
            if (t0 === null) t0 = t;
            var k = Math.min(1, (t - t0) / seg);
            var e = k < .5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
            setPos(from + (keys[i + 1] - from) * e);
            if (k === 1) { i++; from = keys[i]; t0 = null; if (i >= keys.length - 1) return; }
            requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        }, 350);
      });
    }
    var stats = root.querySelector('.pgm-stats');
    if (stats) watch(stats);
  }


  /* ---------- 10. Live review score next to the Judge.me carousel ----------
     Built from Judge.me's own figures (data-score, data-number-of-reviews), so it is always live.
     Replaces the small stars and "from N reviews" line under the carousel title. */
  var STAR = '\u2605\u2605\u2605\u2605\u2605';
  function buildScore(score, count) {
    var a = d.createElement('a');
    a.className = 'pgm-score';
    a.href = '/pages/reviews';
    a.style.setProperty('--fill', (score * 20).toFixed(1) + '%');
    a.setAttribute('aria-label', score.toFixed(2) + ' out of 5 from ' + count + ' verified reviews. Read all reviews');
    a.innerHTML = '<span class="pgm-score__big" aria-hidden="true"><b></b><small>/5</small></span>' +
      '<span class="pgm-score__side" aria-hidden="true"><span class="pgm-score__stars"><span class="pgm-score__stars-on">' + STAR + '</span>' + STAR + '</span>' +
      '<span class="pgm-score__count">from <b></b> verified reviews</span></span>';
    var big = a.querySelector('.pgm-score__big b');
    big.textContent = score.toFixed(2);
    var cnt = a.querySelector('.pgm-score__count b');
    cnt.textContent = String(count);
    big.dataset.pgmSeen = cnt.dataset.pgmSeen = '1';
    if (reduce) a.classList.add('pgm-in');
    else watch(a, function () { countUp(big); countUp(cnt); });
    return a;
  }
  function setupScore() {
    /* Carousels (homepage, collection, selling pages) */
    $$('.jdgm-carousel-title-and-link').forEach(function (head) {
      if (head.dataset.pgmScored) return;
      var rating = head.querySelector('[data-score]');
      var num = head.querySelector('[data-number-of-reviews]');
      var wrap = head.querySelector('.jdgm-all-reviews-rating-wrapper');
      if (!rating || !num || !wrap) return;
      var score = parseFloat(rating.getAttribute('data-score'));
      var count = parseInt(num.getAttribute('data-number-of-reviews'), 10);
      /* PG 4 Nov 2026 (Daniel): on product pages the carousel score is that product's own rating and count
         (window.pgProdScore, set in snippets/pg-fixes-3nov from the product's Judge.me figures), not all products. */
      var ps = window.pgProdScore;
      if (ps && parseInt(ps.c, 10) > 0 && parseFloat(ps.r) > 0 && head.closest('.shopify-section[id$="__pg_rev_carousel"], .pgp-revcar')) { score = parseFloat(ps.r); count = parseInt(ps.c, 10); }
      if (!(score > 0) || !(count > 0)) return;
      head.dataset.pgmScored = '1';
      wrap.style.display = 'none';
      /* Daniel 30 Oct: where the customer reviews image sits straight above, the carousel's own
         "More Pure Glow Reviews" title repeats it, so it goes and the score leads instead */
      var sec = head.closest('.shopify-section');
      var prev = sec && sec.previousElementSibling;
      if (prev && prev.querySelector('.banner')) {
        var t = head.querySelector('.jdgm-carousel-title');
        if (t) t.style.display = 'none';
        head.classList.add('pgm-notitle');
      }
      wrap.parentNode.insertBefore(buildScore(score, count), wrap.nextSibling);
    });
    /* Full review widgets (reviews page, product pages): score card plus bars that fill */
    $$('[data-average-rating][data-number-of-reviews]').forEach(function (head) {
      if (head.dataset.pgmScored) return;
      var inner = head.querySelector('.jdgm-rev-widg__summary-inner');
      var score = parseFloat(head.getAttribute('data-average-rating'));
      var count = parseInt(head.getAttribute('data-number-of-reviews'), 10);
      if (!inner || !(score > 0) || !(count > 0)) return;
      head.dataset.pgmScored = '1';
      Array.prototype.forEach.call(inner.children, function (c) { c.style.display = 'none'; });
      var card = buildScore(score, count);
      card.classList.add('pgm-score--widget');
      inner.appendChild(card);
      var bars = $$('.jdgm-histogram__bar-content', head);
      if (bars.length && !reduce && io) {
        bars.forEach(function (b, i) {
          b.dataset.pgmW = b.style.width || getComputedStyle(b).width;
          b.style.transition = 'width 1.2s cubic-bezier(.2,.7,.2,1) ' + (i * 0.08) + 's';
          b.style.width = '0';
        });
        var hist = head.querySelector('.jdgm-histogram') || bars[0].parentNode;
        watch(hist, function () { bars.forEach(function (b) { b.style.width = b.dataset.pgmW; }); });
      }
    });
  }


  /* ---------- R3. Images sharpen in as they load ---------- */
  function setupImages() {
    if (reduce) return;
    var vh = window.innerHeight;
    $$('#MainContent img, .footer img').forEach(function (img) {
      if (img._pgmImg) return;
      img._pgmImg = true;
      if (img.complete && img.naturalWidth) return;
      if (img.closest('.pgm-ba, .jdgm-widget, .deferred-media, .pgm-score')) return;
      if (img.getBoundingClientRect().top < vh) return; /* first screen: never blur the hero */
      img.classList.add('pgm-img-anim', 'pgm-img-wait');
      function done() {
        img.classList.remove('pgm-img-wait');
        setTimeout(function () { img.classList.remove('pgm-img-anim'); }, 1100);
      }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  /* ---------- R4. Add to cart: "Added" tick on the button, cart icon bounce ---------- */
  var ADD = '.product-form__submit, .quick-add__submit, .pg-sticky__button, .pg-buy-now';
  var lastAdd = null, lastAddAt = 0;
  function cartCount() {
    var b = d.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]') || d.querySelector('#cart-icon-bubble .cart-count-bubble');
    return b ? parseInt(b.textContent, 10) || 0 : 0;
  }
  function showAdded(btn) {
    if (!btn || reduce) return;
    var cs = getComputedStyle(btn);
    if (cs.position === 'static') btn.style.position = 'relative';
    var o = btn.querySelector(':scope > .pgm-added');
    if (!o) {
      o = d.createElement('span');
      o.className = 'pgm-added';
      o.setAttribute('aria-hidden', 'true');
      o.innerHTML = '<svg viewBox="0 0 24 24"><path pathLength="1" d="m5 12.5 4.5 4.5L19 7.5"/></svg>Added';
      btn.appendChild(o);
    }
    requestAnimationFrame(function () { o.classList.add('is-on'); });
    clearTimeout(o._t);
    o._t = setTimeout(function () { o.classList.remove('is-on'); }, 1700);
  }
  /* The cart drawer opens over the page, so it gets its own "Added" tick next to "Your cart" */
  function drawerAdded() {
    var drawer = d.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    var head = drawer.querySelector('.drawer__heading');
    if (!head) return;
    var old = drawer.querySelector('.pgm-drawer-added');
    if (old) old.remove();
    var pill = d.createElement('span');
    pill.className = 'pgm-drawer-added';
    pill.setAttribute('role', 'status');
    pill.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path pathLength="1" d="m5 12.5 4.5 4.5L19 7.5"/></svg>Added';
    head.appendChild(pill);
    requestAnimationFrame(function () { requestAnimationFrame(function () { pill.classList.add('is-on'); }); });
    setTimeout(function () { pill.classList.remove('is-on'); setTimeout(function () { pill.remove(); }, 500); }, 2600);
  }
  function setupCartFx() {
    d.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest(ADD);
      if (b) { lastAdd = b; lastAddAt = Date.now(); }
    }, true);
    var bubble = d.getElementById('cart-icon-bubble');
    if (!bubble || !('MutationObserver' in window)) return;
    var before = cartCount();
    new MutationObserver(function () {
      var now = cartCount();
      if (now > before) {
        if (!reduce) {
          bubble.classList.remove('pgm-bump');
          void bubble.offsetWidth;
          bubble.classList.add('pgm-bump');
          setTimeout(function () { bubble.classList.remove('pgm-bump'); }, 900);
        }
        if (lastAdd && Date.now() - lastAddAt < 8000) showAdded(lastAdd);
        if (!reduce) setTimeout(drawerAdded, 60);
      }
      before = now;
    }).observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  /* ---------- R6. Product gallery magnifier (desktop) ---------- */
  function setupZoom() {
    if (!(window.matchMedia && matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)').matches)) return;
    $$('.product__media-item .product-media-container.media-type-image').forEach(function (box) {
      if (box._pgmZoom) return;
      box._pgmZoom = true;
      var img = box.querySelector('.media img');
      if (!img) return;
      box.addEventListener('mouseenter', function () { box.classList.add('pgm-zoom'); });
      box.addEventListener('mouseleave', function () { box.classList.remove('pgm-zoom'); img.style.transformOrigin = ''; });
      box.addEventListener('mousemove', function (e) {
        var r = box.getBoundingClientRect();
        img.style.transformOrigin = ((e.clientX - r.left) / r.width * 100).toFixed(1) + '% ' + ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%';
      });
    });
  }

  /* ---------- R7. Shade scale fills as it scrolls past ---------- */
  function setupShade() {
    $$('[data-pgm-shade]').forEach(function (el) {
      if (el._pgm) return;
      el._pgm = true;
      var track = el.querySelector('.pgm-shade__track');
      var chips = $$('.pgm-shade__chip', el);
      var last = -1;
      function paint(k) {
        var first = chips[0], end = chips[chips.length - 1];
        var a = first.offsetLeft + first.offsetWidth / 2, b = end.offsetLeft + end.offsetWidth / 2;
        track.style.setProperty('--mx', (a + (b - a) * k).toFixed(1) + 'px');
        var idx = Math.round(k * (chips.length - 1));
        if (idx !== last) {
          chips.forEach(function (c, i) { c.classList.toggle('is-on', i === idx); });
          last = idx;
        }
      }
      if (reduce) { paint(1); return; }
      var ticking = false;
      function update() {
        ticking = false;
        var r = el.getBoundingClientRect(), vh = window.innerHeight;
        /* 0 as it enters at the bottom, 1 once it is two-fifths of the way up the screen */
        var k = (vh - r.top) / (vh * 0.6);
        paint(Math.max(0, Math.min(1, k)));
      }
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- R9. Swipe rows on phones get a progress line ---------- */
  function setupSwipe() {
    $$('.multicolumn .slider, .collection .slider, .blog .slider').forEach(function (list) {
      if (!/slider--(mobile|tablet)/.test(list.className)) return;
      if (list._pgmSwipe) return;
      list._pgmSwipe = true;
      var bar = d.createElement('div');
      bar.className = 'pgm-swipe';
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<span class="pgm-swipe__thumb"></span>';
      var host = list.closest('slider-component') || list;
      /* Sit between Dawn's arrows, in place of the "1/4" counter */
      var counter = host.querySelector('.slider-buttons .slider-counter');
      if (counter) {
        counter.style.display = 'none';
        bar.classList.add('pgm-swipe--inline');
        counter.parentNode.insertBefore(bar, counter);
      } else {
        host.parentNode.insertBefore(bar, host.nextSibling);
      }
      function update() {
        var max = list.scrollWidth - list.clientWidth;
        if (max <= 4) { bar.style.display = 'none'; return; }
        bar.style.display = '';
        var w = list.clientWidth / list.scrollWidth;
        bar.style.setProperty('--w', (w * 100).toFixed(1) + '%');
        bar.style.setProperty('--x', ((list.scrollLeft / max) * (1 / w - 1)).toFixed(3));
      }
      list.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }


  /* ---------- 31 Oct (Daniel): every section title in title case, with its key words in the purple accent ----------
     The titles below are typed in the theme editor, so they are styled here rather than rewritten in
     the page settings. Only the casing and an <em> are added; the words are the same. */
  var TITLES = {
    "pure glow's key benefits": "Pure Glow's <em class=\"pgm-accent\">Key Benefits</em>",
    "stay consistent & maintain your results": "Stay Consistent &amp; <em class=\"pgm-accent\">Maintain Your Results</em>",
    "common questions": "Common <em class=\"pgm-accent\">Questions</em>",
    "the pure glow blog": "The <em class=\"pgm-accent\">Pure Glow Blog</em>",
    "shop pure glow whitening strips": "Shop Pure Glow <em class=\"pgm-accent\">Whitening Strips</em>",
    "try pure glow with confidence": "Try Pure Glow With <em class=\"pgm-accent\">Confidence</em>",
    "teeth whitening strips that stay put and peel off clean": "Teeth Whitening Strips That <em class=\"pgm-accent\">Stay Put And Peel Off Clean</em>",
    "teeth whitening strips: common questions": "Teeth Whitening Strips: <em class=\"pgm-accent\">Common Questions</em>",
    "also available": "Also <em class=\"pgm-accent\">Available</em>",
    "customer reviews": "Customer <em class=\"pgm-accent\">Reviews</em>",
    "more pure glow reviews": "More <em class=\"pgm-accent\">Pure Glow Reviews</em>"
  };
  function setupTitles() {
    $$('#MainContent h1.collection-hero__title, #MainContent h2, #MainContent .title, #MainContent .rich-text__heading, #MainContent .jdgm-rev-widg__title').forEach(function (h) {
      if (h.querySelector('.pgm-accent')) return; /* 31 Oct: not _pgmT, as Judge.me rewrites its title after we style it */
      var key = h.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (TITLES[key]) { h._pgmT = true; h.innerHTML = TITLES[key]; }
    });
  }


  /* 31 Oct (Daniel): every drop-down that holds a question looks exactly like the homepage Common Questions:
     the question-mark icon on the left, the same pill. Non-question drop-downs (delivery, ingredients) are left alone. */
  var QSVG = '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-accordion icon-question-mark" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M9.563 11.959a.367.367 0 0 1-.367-.367v-.127c-.003-.49.084-.91.316-1.303.227-.384.575-.715 1.04-1.063.415-.311.666-.566.817-.81a1.43 1.43 0 0 0 .208-.795v-.013c0-.894-.657-1.531-1.568-1.531-.862 0-1.527.606-1.612 1.609a.37.37 0 0 1-.097.219l-.006.006a.37.37 0 0 1-.264.117l-.471.007a.367.367 0 0 1-.362-.453c.112-1.519 1.219-2.712 2.825-2.712 1.603 0 2.791 1.176 2.791 2.718v.013c0 .512-.114.965-.366 1.387-.248.416-.619.783-1.101 1.145l-.003.002c-.414.305-.637.526-.764.738-.12.2-.172.422-.172.771v.075a.367.367 0 0 1-.367.367zm1.142 2.423c0 .473-.335.88-.838.88-.497 0-.845-.4-.845-.88 0-.477.351-.874.845-.874.5 0 .838.403.838.874"/><path d="M18.5 10a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0M10 17.497a7.497 7.497 0 1 0 0-14.994 7.497 7.497 0 0 0 0 14.994"/></svg>';
  function setupQuestions() {
    $$('.accordion details').forEach(function (dt) {
      var s = dt.querySelector('summary');
      if (!s) return;
      var t = s.querySelector('.accordion__title, h3, h2');
      var txt = (t || s).textContent.replace(/\s+/g, ' ').trim();
      if (!/\?$/.test(txt)) return;
      var acc = dt.closest('.accordion');
      acc.classList.add('pgm-q');
      if (!s.querySelector('.icon-accordion')) {
        var w = document.createElement('span');
        w.className = 'svg-wrapper pgm-qicon';
        w.innerHTML = QSVG;
        s.insertBefore(w, s.firstChild);
      }
    });
  }

  /* 31 Oct (Daniel): Judge.me's "Write a review" is a site button, white with the silver rim and purple text, like every
     button that is not Add to cart */
  function setupJdgmBtn() {
    $$('.jdgm-write-rev-link').forEach(function (a) { if (!a.classList.contains('button')) a.classList.add('button', 'pgm-jdgm-btn'); });
  }

  /* 1 Nov (Daniel): the homepage founder banner gets a proper heading, "A Message From Our Founder", in place of the small label */
  function setupFounder() {
    /* 1 Nov: the collection page's founder banner (image_banner_wK83Xg) gets the same heading */
    $$('.shopify-section[id*="image_banner_gQyRwb"], .shopify-section[id*="image_banner_wK83Xg"]').forEach(function (s) {
      if (s.querySelector('.pgm-founder-title')) return;
      var h = d.createElement('h2');
      h.className = 'h1 pgm-founder-title';
      h.innerHTML = 'A Message From <em class="pgm-accent">Our Founder</em>';
      s.insertBefore(h, s.firstChild);
    });
  }


  /* 1 Nov (Daniel): every other page in the same design. On /pages/ that are not selling pages: the key words of each
     section heading in purple (not on the press, privacy or terms pages, whose headings are names and clauses), and a link
     on a line of its own becomes the white site button. The words are unchanged. */
  var ENDS = ['Pure Glow Teeth Whitening', 'Teeth Whitening Strips', 'Pure Glow Strips', 'Teeth Whitening', 'Pure Glow', 'Before And After'];
  function accentEnd(h) {
    if (h.querySelector('.pgm-accent')) return;
    var walk = d.createTreeWalker(h, NodeFilter.SHOW_TEXT), n, list = [];
    while ((n = walk.nextNode())) if (n.nodeValue.trim()) list.push(n);
    if (!list.length) return;
    n = list[list.length - 1];
    var txt = n.nodeValue.replace(/\s+$/, '');
    var full = h.textContent.replace(/\s+/g, ' ').trim();
    if (full.split(' ').length < 2) return;
    var core = txt.replace(/[?.!:]+$/, '');
    var phrase = null;
    /* 1 Nov: a name then a comma ("Rose, After Two Uses"): the whole part after the comma is the accent */
    var cm = core.match(/,\s+([^,]+)$/);
    if (cm && cm[1].split(' ').length >= 2 && cm[1].split(' ').length <= 5 && txt.indexOf(cm[1]) > -1) phrase = cm[1];
    for (var i = 0; !phrase && i < ENDS.length; i++) {
      if (core.toLowerCase().slice(-ENDS[i].length) === ENDS[i].toLowerCase() && core.length > ENDS[i].length) { phrase = core.slice(-ENDS[i].length); break; }
    }
    if (!phrase) {
      /* the last real word, carrying any small trailing words or a number with it ("stands for", "March 2026") */
      var words = core.split(' '), k = words.length - 1;
      var SMALL = /^(for|to|of|in|on|at|with|us|it|is|a|an|the|you|up|and|or|by|from|\d[\d,.]*)$/i;
      while (k > 0 && SMALL.test(words[k])) k--;
      if (k <= 0) return;
      if (k > 1 && /^\d[\d,.]*$/.test(words[k - 1])) k--;
      phrase = words.slice(k).join(' ');
      if (phrase.length >= core.length) return;
    }
    var at = core.length - phrase.length;
    var mid = n.splitText(at);
    mid.splitText(phrase.length);
    var em = d.createElement('em');
    em.className = 'pgm-accent';
    mid.parentNode.replaceChild(em, mid);
    em.appendChild(mid);
  }
  function setupPages() {
    var canon = (d.querySelector('link[rel="canonical"]') || {}).href || location.href;
    if (!/\/pages\//.test(canon) || d.querySelector('.pg-landing')) return;
    var noAccent = /\/pages\/(press|privacy-policy|terms-conditions)\b/.test(canon);
    var hs = $$('.page-width--narrow > .main-page-title ~ .rte > h2, .shopify-section[id*="__pg_all_reviews"] .page-width > h2:not(.accordion__title), .shopify-section[id*="__image_with_text_j6KqTc"] .image-with-text__text > h2, .shopify-section[id*="__rich_text_"] .rich-text__heading, .collapsible-content__heading');
    if (!noAccent) hs.forEach(accentEnd);
    /* lists of warnings get a "!" rather than a tick */
    $$('.page-width--narrow > .main-page-title ~ .rte > ul').forEach(function (ul) {
      var h = ul.previousElementSibling;
      while (h && !/^H[23]$/.test(h.tagName)) h = h.previousElementSibling;
      if (h && /warning|not suitable|avoid|void/i.test(h.textContent)) ul.classList.add('pgm-ul-warn');
    });
    $$('.page-width--narrow > .main-page-title ~ .rte > p, .shopify-section[id*="__pg_all_reviews"] .page-width > p').forEach(function (p) {
      if (p.classList.contains('pgm-linkp')) return;
      var kids = p.children;
      if (kids.length !== 1 || kids[0].tagName !== 'A') return;
      var a = kids[0];
      if (p.textContent.trim() !== a.textContent.trim() || a.querySelector('img')) return;
      p.classList.add('pgm-linkp');
      if (a.textContent.trim().length > 26) p.classList.add('pgm-linkp--long');
      a.classList.add('button');
    });
    setupPolicies(canon);
    setupPress(canon);
    setupAbout(canon);
    setupContact(canon);
  }


  /* =====================================================================
     1 Nov 2026 (Daniel): round 4. Buttons, page redesigns, blog, motion.
     Display only: words, links and heading tags in the page source are unchanged.
     ===================================================================== */
  var PGE = 'cubic-bezier(.22,.61,.36,1)';

  /* A link on a line of its own becomes the white site button */
  function toButton(p, label) {
    if (!p || p.classList.contains('pgm-linkp')) return;
    var a = p.querySelector('a');
    if (!a || p.textContent.trim() !== a.textContent.trim()) return;
    if (label) { a.setAttribute('aria-label', a.textContent.trim()); a.textContent = label; }
    p.classList.add('pgm-linkp');
    if (a.textContent.trim().length > 26) p.classList.add('pgm-linkp--long');
    a.classList.add('button');
  }
  function setupLinkButtons() {
    /* product pages: "Read all reviews" and "See real before and after photos" */
    $$('.shopify-section[id*="__pg_review_links"] .rte > p').forEach(function (p) { toButton(p); });
    $$('.shopify-section[id*="__pg_review_links"] .rte').forEach(function (r) { r.classList.add('pgm-linkrow'); });
    /* guarantee panels: the paragraph above already names the guarantee, so the button just says what it opens */
    $$('#MainContent .rich-text__text p').forEach(function (p) {
      var a = p.querySelector('a');
      if (a && /guarantee terms/i.test(a.textContent) && p.textContent.trim() === a.textContent.trim()) toButton(p, 'See The Full Terms');
    });
  }

  /* Back to top: a slim pill that appears once you are well down the page, and sits above any buy bar */
  function setupTop() {
    var b = d.createElement('button');
    b.type = 'button';
    b.className = 'pgm-top';
    b.setAttribute('aria-label', 'Back to top');
    b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5m-6 6 6-6 6 6"/></svg><span>Top</span>';
    d.body.appendChild(b);
    b.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
      var t = d.querySelector('#MainContent');
      if (t) setTimeout(function () { t.setAttribute('tabindex', '-1'); t.focus({ preventScroll: true }); }, reduce ? 0 : 600);
    });
    var ticking = false;
    function update() {
      ticking = false;
      var y = window.scrollY || window.pageYOffset, vh = window.innerHeight;
      var on = y > vh * 1.6;
      var lift = 0;
      $$('.pg-sticky').forEach(function (bar) {
        if (getComputedStyle(bar).display === 'none' || getComputedStyle(bar).visibility === 'hidden') return;
        var r = bar.getBoundingClientRect();
        if (r.height && r.top < vh && r.bottom > vh - 4) lift = Math.max(lift, vh - r.top);
      });
      b.style.setProperty('--pgm-lift', lift + 'px');
      b.classList.toggle('is-on', on);
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* Reviews page: the score and photos first, then six reviews, with "Load more reviews" for the rest */
  function setupRevMore() {
    var w = d.querySelector('.jdgm-all-reviews-page .jdgm-all-reviews-widget');
    if (!w) return;
    var body = w.querySelector('.jdgm-all-reviews__body');
    if (!body || body._pgmMore) return;
    if ($$(':scope > .jdgm-rev', body).length <= 6) return;
    body._pgmMore = true;
    var shown = 6, foot = w.querySelector('.jdgm-all-reviews__footer');
    var wrap = d.createElement('p');
    wrap.className = 'pgm-linkp pgm-revmore';
    wrap.innerHTML = '<button type="button" class="button">Load more reviews</button>';
    body.parentNode.insertBefore(wrap, body.nextSibling);
    /* Judge.me adds more reviews as you scroll, so anything past the count shown is kept back until asked for */
    function apply(animateFrom) {
      var revs = $$(':scope > .jdgm-rev', body);
      revs.forEach(function (r, i) {
        var hide = i >= shown;
        var was = r.classList.contains('pgm-rev-hide');
        r.classList.toggle('pgm-rev-hide', hide);
        if (!hide && was && animateFrom !== undefined && !reduce && r.animate) {
          r.animate({ opacity: [0, 1], transform: ['translateY(14px)', 'none'] }, { duration: 520, delay: (i - animateFrom) * 70, easing: PGE, fill: 'backwards' });
        }
      });
      var more = revs.length > shown;
      wrap.style.display = more ? '' : 'none';
      if (foot) foot.classList.toggle('pgm-rev-hide', more);
    }
    apply();
    new MutationObserver(function () { apply(); }).observe(body, { childList: true });
    wrap.firstChild.addEventListener('click', function () { var from = shown; shown += 6; apply(from); });
  }

  /* Shipping and returns, privacy, terms: each section in the site drop-down. The heading itself moves into the
     drop-down title, so the heading tags and every word stay in the page. */
  var CARET = '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';
  function setupPolicies(canon) {
    var m = canon.match(/\/pages\/(shipping-returns|privacy-policy|terms-conditions)\b/);
    if (!m) return;
    var rte = d.querySelector('.page-width--narrow > .main-page-title ~ .rte');
    if (!rte || rte.querySelector('.pgm-pol')) return;
    var tag = m[1] === 'shipping-returns' ? 'H2' : 'H3';
    var kids = Array.prototype.slice.call(rte.children);
    var first = kids.filter(function (k) { return k.tagName === tag; })[0];
    if (!first) return;
    var box = d.createElement('div');
    box.className = 'pgm-pol';
    rte.insertBefore(box, first);
    var cur = null, n = 0;
    kids.forEach(function (k) {
      if (k === box) return;
      if (k.tagName === tag) {
        n++;
        var acc = d.createElement('div');
        acc.className = 'accordion pgm-pol__acc';
        acc.innerHTML = '<details id="PgPol-' + n + '"><summary><div class="summary__title"></div>' + CARET + '</summary><div class="accordion__content rte"></div></details>';
        k.classList.add('accordion__title', 'h4');
        acc.querySelector('.summary__title').appendChild(k);
        cur = acc.querySelector('.accordion__content');
        box.appendChild(acc);
      } else if (cur && kids.indexOf(k) > kids.indexOf(first)) {
        cur.appendChild(k);
      }
    });
    /* a link to a section (#returns, say) opens it */
    function openHash() {
      if (!location.hash) return;
      var t = d.getElementById(location.hash.slice(1));
      var det = t && t.closest && t.closest('.pgm-pol details');
      if (det) det.open = true;
    }
    openHash();
    window.addEventListener('hashchange', openHash);
  }

  /* Press: each publication on its own silver-framed card, sliding in from alternate sides */
  function setupPress(canon) {
    if (!/\/pages\/press\b/.test(canon)) return;
    var rte = d.querySelector('.page-width--narrow > .main-page-title ~ .rte');
    if (!rte || rte.querySelector('.pgm-press')) return;
    var kids = Array.prototype.slice.call(rte.children);
    var cur = null, n = 0;
    kids.forEach(function (k) {
      if (k.tagName === 'H2') {
        cur = d.createElement('div');
        cur.className = 'pgm-press ' + (n++ % 2 ? 'pgm-press--r' : 'pgm-press--l');
        rte.insertBefore(cur, k);
        cur.appendChild(k);
      } else if (cur) {
        cur.appendChild(k);
      }
    });
    if (!reduce) $$('.pgm-press', rte).forEach(function (c) { watch(c); });
  }

  /* About: the mission paragraphs and Daniel's sign-off as a quote card, and a short facts row under the title */
  function setupAbout(canon) {
    if (!/\/pages\/about-us\b/.test(canon)) return;
    var rte = d.querySelector('.page-width--narrow > .main-page-title ~ .rte');
    if (!rte || rte.querySelector('.pgm-quote')) return;
    var ps = $$(':scope > p', rte);
    var sig = ps.filter(function (p) { return /^Daniel Smith, Founder/.test(p.textContent.trim()); })[0];
    var start = ps.filter(function (p) { return /restoring trust/i.test(p.textContent); })[0];
    if (sig && start) {
      var q = d.createElement('div');
      q.className = 'pgm-quote';
      rte.insertBefore(q, start);
      var k = start;
      while (k) { var nx = k.nextElementSibling; q.appendChild(k); if (k === sig) break; k = nx; }
      sig.classList.add('pgm-quote__sig');
      if (!reduce) watch(q);
    }
    var facts = d.createElement('ul');
    facts.className = 'pgm-facts';
    facts.setAttribute('aria-label', 'Pure Glow at a glance');
    facts.innerHTML =
      '<li><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="3"/><path d="M3.5 10h17M8 3v4m8-4v4"/></svg>Founded 2024</li>' +
      '<li><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z"/><circle cx="12" cy="10" r="2.4"/></svg>Coventry, UK</li>' +
      '<li><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>15 years at Jaguar Land Rover</li>';
    rte.insertBefore(facts, rte.firstChild);
  }

  /* Contact: Customer Support and General Enquiries as two cards with icons, above the form */
  function setupContact(canon) {
    if (!/\/pages\/contact/.test(canon)) return;
    var rte = d.querySelector('.page-width--narrow > .main-page-title ~ .rte');
    if (!rte || rte.querySelector('.pgm-ccards')) return;
    var ICON = {
      support: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 13.5v-1.8a7.5 7.5 0 0 1 15 0v1.8"/><rect x="3.5" y="13" width="3.6" height="5.6" rx="1.4"/><rect x="16.9" y="13" width="3.6" height="5.6" rx="1.4"/><path d="M18.7 18.6c0 1.4-1.6 2.4-4.2 2.4h-1.8"/></svg>',
      general: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5.5" width="18" height="13" rx="2.6"/><path d="m4 7.5 8 6 8-6"/></svg>'
    };
    var cards = $$(':scope > p', rte).filter(function (p) { return /^(Customer Support|General Enquiries)/.test(p.textContent.trim()); });
    if (cards.length < 2) return;
    var row = d.createElement('div');
    row.className = 'pgm-ccards';
    rte.insertBefore(row, cards[0]);
    cards.forEach(function (p) {
      p.classList.add('pgm-ccard');
      var i = d.createElement('span');
      i.className = 'pgm-ccard__icon';
      i.innerHTML = /^Customer/.test(p.textContent.trim()) ? ICON.support : ICON.general;
      p.insertBefore(i, p.firstChild);
      /* the title is on its own line on the card, so the colon after it goes */
      var st = p.querySelector('strong'), nx = st && st.nextSibling;
      if (nx && nx.nodeType === 3 && /^\s*:/.test(nx.nodeValue)) nx.nodeValue = nx.nodeValue.replace(/^\s*:\s*/, '');
      row.appendChild(p);
    });
  }

  /* Blog: the blog title with the accent, and Daniel's author card at the end of every article */
  function setupBlog() {
    $$('.main-blog h1.title--primary, .blog h1.title--primary').forEach(function (h) {
      if (h.querySelector('.pgm-accent')) return;
      var t = h.textContent.replace(/\s+/g, ' ').trim();
      var w = t.split(' ');
      if (w.length < 2) { h.innerHTML = '<em class="pgm-accent">' + h.innerHTML.trim() + '</em>'; return; }
      accentEnd(h);
    });
    var art = d.querySelector('.article-template__content');
    if (art && !d.querySelector('.pgm-author')) {
      var c = d.createElement('aside');
      c.className = 'pgm-author';
      c.setAttribute('aria-label', 'About the author');
      c.innerHTML =
        '<span class="pgm-author__mark" aria-hidden="true">DS</span>' +
        '<div class="pgm-author__body">' +
          '<p class="pgm-author__name">Daniel Smith</p>' +
          '<p class="pgm-author__role">Founder, Pure Glow Teeth Whitening</p>' +
          '<p class="pgm-author__text">Daniel started Pure Glow in 2024 after years of whitening strips that left his teeth sensitive and slid around, and built a peroxide-free PAP+ formula with hydroxyapatite instead. Before that he spent 15 years at Jaguar Land Rover.</p>' +
        '</div>' +
        '<p class="pgm-author__cta"><a class="button" href="/pages/about-us">Read Daniel’s Story</a></p>';
      art.parentNode.insertBefore(c, art.nextSibling);
      if (!reduce) watch(c);
    }
  }

  /* ---------- Motion, round 4 ---------- */
  /* Ticks draw in one after another */
  function setupTicks() {
    if (reduce) return;
    $$('.product__description ul.pgp-kit, .pg-landing__body ul, .page-width--narrow:not(.pg-landing) > .main-page-title ~ .rte ul, .shopify-section[id*="__image_with_text_j6KqTc"] .image-with-text__text ul').forEach(function (ul) {
      if (ul._pgmTicks) return;
      ul._pgmTicks = true;
      $$(':scope > li', ul).forEach(function (li, i) { li.style.setProperty('--pgm-i', Math.min(i, 10)); });
      ul.classList.add('pgm-ticks');
      watch(ul);
    });
  }

  /* Comparison table: rows arrive one by one and the saving counts up */
  function moneyCount(el) {
    var raw = el.textContent.trim();
    var m = raw.match(/^([^\d]*)(\d[\d.,\s]*\d|\d)([^\d]*)$/);
    if (!m) return;
    var num = m[2], dec = 0, sep = '.';
    var dm = num.match(/[.,](\d{2})$/);
    if (dm) { dec = 2; sep = num.charAt(num.length - 3); }
    var target = parseFloat(num.replace(/[^\d]/g, '')) / (dec ? 100 : 1);
    if (!target) return;
    var start = null;
    function fmt(v) { var s2 = v.toFixed(dec); return m[1] + (sep === ',' ? s2.replace('.', ',') : s2) + m[3]; }
    el.textContent = fmt(0);
    function step(t) {
      if (start === null) start = t;
      var k = Math.min(1, (t - start) / 1200);
      el.textContent = k < 1 ? fmt(target * (1 - Math.pow(1 - k, 3))) : raw;
      if (k < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function setupCompare() {
    if (reduce) return;
    $$('.pgc-table').forEach(function (t) {
      if (t._pgm) return;
      t._pgm = true;
      $$('.pgc-row', t).forEach(function (r, i) { r.style.setProperty('--pgm-i', i); });
      t.classList.add('pgm-rows');
      var save = t.querySelector('.pgc-save');
      watch(t, function () { if (save) setTimeout(function () { moneyCount(save); }, 520); });
    });
  }

  /* Guarantee panels: a single soft light passes across when they come into view */
  function setupShimmer() {
    if (reduce) return;
    var list = $$('.pg-landing .pgl-guarantee');
    $$('#MainContent .rich-text__blocks').forEach(function (b) {
      var h = b.querySelector('.rich-text__heading');
      if (h && /with confidence/i.test(h.textContent)) list.push(b);
    });
    list.forEach(function (el) {
      if (el._pgmShim) return;
      el._pgmShim = true;
      el.classList.add('pgm-shim-host');
      var s = d.createElement('span');
      s.className = 'pgm-shim';
      s.setAttribute('aria-hidden', 'true');
      el.appendChild(s);
      watch(el);
    });
  }

  /* Accent words settle in a beat after their heading */
  function setupAccentFx() {
    if (reduce) return;
    $$('#MainContent h1 .pgm-accent, #MainContent h2 .pgm-accent, #MainContent .rich-text__heading .pgm-accent').forEach(function (em) {
      var h = em.closest('h1, h2, .rich-text__heading');
      if (!h || h._pgmAcc) return;
      h._pgmAcc = true;
      if (h.getBoundingClientRect().top < window.innerHeight * 0.9 && (window.scrollY || 0) < 10 && !h.closest('.pgm-rise')) return; /* first screen on arrival: shown as it is */
      h.classList.add('pgm-acc');
      watch(h);
    });
  }

  /* Cart drawer: items arrive one after another and the two-box upgrade glows once */
  function setupDrawerFx() {
    if (reduce || !('MutationObserver' in window)) return;
    var dr = d.querySelector('cart-drawer');
    if (!dr) return;
    var was = dr.classList.contains('active');
    new MutationObserver(function () {
      var on = dr.classList.contains('active');
      if (on && !was) {
        setTimeout(function () {
          $$('.cart-item', dr).forEach(function (row, i) {
            if (row.animate) row.animate({ opacity: [0, 1], transform: ['translateY(12px)', 'none'] }, { duration: 480, delay: 120 + i * 90, easing: PGE, fill: 'backwards' });
          });
          var up = dr.querySelector('.pg-upsell');
          if (up) {
            up.classList.remove('pgm-pulse');
            void up.offsetWidth;
            up.classList.add('pgm-pulse');
            setTimeout(function () { up.classList.remove('pgm-pulse'); }, 2200);
          }
        }, 40);
      }
      was = on;
    }).observe(dr, { attributes: true, attributeFilter: ['class'] });
  }


  /* Pre-launch audit: some sections tell the browser their pictures are half the screen wide when on phones they are
     full width (Key Benefits cards, blog cards), so phones downloaded a picture too small and it looked soft. Each
     picture's "sizes" is set to the width it is actually shown at, and the browser fetches the sharper copy. */
  function setupSharp() {
    $$('#MainContent img[srcset], .footer img[srcset]').forEach(function (img) {
      if (img._pgmSharp) return;
      var w = img.getBoundingClientRect().width;
      if (w < 120) return;
      var max = 0;
      img.getAttribute('srcset').split(',').forEach(function (c) { var m = c.trim().match(/\s(\d+)w$/); if (m) max = Math.max(max, +m[1]); });
      var dpr = Math.min(window.devicePixelRatio || 1, 3);
      if (img.naturalWidth && img.naturalWidth >= Math.min(max, w * dpr * 0.95)) return;
      img._pgmSharp = true;
      img.setAttribute('sizes', Math.ceil(w) + 'px');
    });
  }


  /* 1 Nov (Daniel): "FREE Teeth Whitening eBook" reads "Free Teeth Whitening eBook" in the menus, like the other items.
     Display only: the menu itself, its link and the page are unchanged. */
  function setupMenuLabels() {
    $$('.header__inline-menu a, .menu-drawer a, .footer a').forEach(function (a) {
      var w = d.createTreeWalker(a, NodeFilter.SHOW_TEXT), n;
      while ((n = w.nextNode())) if (/\bFREE\b/.test(n.nodeValue)) n.nodeValue = n.nodeValue.replace(/\bFREE\b/g, 'Free');
    });
  }

  /* ---------- Start ---------- */
  function init() {
    setupQuestions();
    setupTitles();
    setupJdgmBtn();
    setupFounder();
    setupMenuLabels();
    setupLinkButtons();
    setupPages();
    setupBlog();
    setupSections();
    setupIcons();
    setupCounts();
    setupHeader();
    setupSheen();
    setupAccordions();
    $$('[data-pgm-ba]').forEach(setupSlider);
    setupScore();
    setupImages();
    setupCartFx();
    setupZoom();
    setupShade();
    setupSwipe();
    setupTop();
    setupTicks();
    setupCompare();
    setupShimmer();
    setupAccentFx();
    setupDrawerFx();
    setupRevMore();
    window.addEventListener('load', function () { setTimeout(setupSharp, 400); });
    [2500, 6000].forEach(function (ms) { setTimeout(setupSharp, ms); });
    /* Judge.me draws its widgets after the page loads, so watch for them */
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        if (mo._t) return;
        mo._t = setTimeout(function () { mo._t = null; setupTitles(); setupJdgmBtn(); setupScore(); setupCounts(); setupAccordions(); setupImages(); setupZoom(); setupRevMore(); setupAccentFx(); }, 120);
      });
      mo.observe(d.body, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 20000);
    }
    /* Apps such as Judge.me draw their widgets after the page loads */
    [1500, 4000, 8000].forEach(function (ms) {
      setTimeout(function () { setupCounts(); setupIcons(); setupAccordions(); setupRevMore(); }, ms);
    });
    /* 31 Oct: some blocks only load as you scroll to them (the product pages' "Also Available", Judge.me),
       which can be long after the watcher above has stopped, so titles and buttons are checked as you scroll */
    var lateT = 0;
    window.addEventListener('scroll', function () {
      if (lateT) return;
      lateT = setTimeout(function () { lateT = 0; setupTitles(); setupJdgmBtn(); setupAccentFx(); setupRevMore(); setupSharp(); }, 400);
    }, { passive: true });
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init); else init();

  /* Cart drawer and theme editor re-render parts of the page */
  d.addEventListener('shopify:section:load', function () { setupIcons(); setupCounts(); setupAccordions(); $$('[data-pgm-ba]').forEach(setupSlider); });
})();
